import './TrainManager.scss';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { MdClose, MdDelete, MdEdit, MdOutlineAutoAwesome, MdSave } from 'react-icons/md';
import { AdPlatform, InstanceRow } from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { Button } from '../../Components/Button';
import { DatePicker } from '../../Components/DatePicker/DatePicker';
import {
  Appearance,
  Background,
  Design,
  Intent,
  Placement,
  Size
} from '../../Components/designConstants';
import { DownloadDropdown } from '../../Components/DownloadDropdown/DownloadDropdown';
import { Dropdown } from '../../Components/Dropdown';
import { FilterBar } from '../../Components/FilterBar/FilterBar';
import { IconButton } from '../../Components/IconButton';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { FullPageSpinner, Spinner } from '../../Components/Spinner';
import { BPMTable, Header } from '../../Components/StickyTable';
import { ToggleSwitch } from '../../Components/ToggleSwitch/ToggleSwitch';
import { useSetError } from '../../redux/modals';
import { exportToExcel } from '../../utils/download-utils';
import {
  CopyTigerPyLambdaFetch,
  fetchCometSelfInvoke,
  getSafeErrorMessage
} from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  addCopyTigerModelInstance,
  DialogState,
  getPayloadSize,
  LANGUAGE_OPTIONS_MAP,
  PAYLOAD_LIMIT,
  PLATFORM_LABELS,
  SelectedRowsMap,
  TableRow,
  TrainingData
} from './copyTigerUtils';

const getTextWidth = (text: string, fontSize: number, fontWeight: number): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;
  context.font = `${fontWeight} ${fontSize}px sans-serif`;
  return context.measureText(text).width;
};

const buildTrainingAds = (rows: TableRow[]): TrainingData[] =>
  rows.map(row => ({
    headlines: [
      row.headline1,
      row.headline2,
      row.headline3,
      row.headline4,
      row.headline5,
      row.headline6,
      row.headline7,
      row.headline8,
      row.headline9,
      row.headline10,
      row.headline11,
      row.headline12,
      row.headline13,
      row.headline14,
      row.headline15
    ].filter((h): h is string => Boolean(h)),
    descriptions: [row.description1, row.description2, row.description3, row.description4].filter(
      (d): d is string => Boolean(d)
    ),
    keywords: row.keywords.join(',')
  }));

const kickOffAndPollTraining = async (body: {
  lambdaArgs: { company: string; training_ads: TrainingData[] };
}): Promise<object> => {
  const size = getPayloadSize(JSON.stringify(body));
  if (size > PAYLOAD_LIMIT) {
    throw new Error(
      `Too many ads to train on, hitting AWS payload limit. Please train with less ads. Current Payload Size: ${size} bytes. Payload Limit: ${PAYLOAD_LIMIT} bytes.`
    );
  }
  return fetchCometSelfInvoke<typeof body.lambdaArgs, object>(
    CopyTigerPyLambdaFetch,
    '/trainCopyTigerModel',
    body.lambdaArgs,
    { noTimeout: true, period: 3000 }
  );
};

const MAX_COL_WIDTH = 500;
const MIN_COL_WIDTH = 80;
const COL_PADDING = 32;

const ADDITIONAL_HEADERS: Header[] = [
  {
    contentGetter: (row: TableRow) => row.adStrength ?? '',
    label: 'Ad Strength',
    name: 'adStrength'
  }
];

const DESCRIPTION_HEADERS: Header[] = Array.from({ length: 4 }, (_, i) => ({
  contentGetter: (row: TableRow) => (row[`description${i + 1}` as keyof TableRow] as string) ?? '',
  label: `Description ${i + 1}`,
  name: `description${i + 1}`
}));

const HEADLINE_HEADERS: Header[] = Array.from({ length: 15 }, (_, i) => ({
  contentGetter: (row: TableRow) => (row[`headline${i + 1}` as keyof TableRow] as string) ?? '',
  label: `Headline ${i + 1}`,
  name: `headline${i + 1}`
}));

const validateTrainingInputs = ({
  selectedCount,
  modelName,
  modelDescription
}: {
  selectedCount: number;
  modelName: string;
  modelDescription: string;
}): DialogState | null => {
  if (selectedCount === 0) {
    return {
      body: 'Please select at least one row to train with',
      title: 'Error',
      intent: Intent.ERROR
    };
  }
  if (modelDescription.length > 150) {
    return {
      body: 'Model Description cannot exceed 150 characters',
      title: 'Error',
      intent: Intent.ERROR
    };
  }
  if (modelDescription.length === 0) {
    return { body: 'Model Description is required', title: 'Error', intent: Intent.ERROR };
  }
  if (modelName.length === 0) {
    return { body: 'Model Name is required', title: 'Error', intent: Intent.ERROR };
  }
  return null;
};

interface TrainModalProps {
  deleteModel: () => void;
  end: string;
  isLoadingTrainingOptions: boolean;
  mode: 'edit' | 'new';
  onDateRangeChange: (dateRange: { start: string; end: string }) => void;
  onHide: (shouldRefresh?: boolean) => void;
  selectedModel?: InstanceRow;
  setDialog: Dispatch<SetStateAction<DialogState | undefined>>;
  setSelectedModelID: Dispatch<SetStateAction<string>>;
  start: string;
  trainingOptions: TableRow[];
}

export const TrainModal = ({
  deleteModel,
  end,
  isLoadingTrainingOptions,
  mode,
  onDateRangeChange,
  onHide,
  selectedModel,
  setDialog,
  setSelectedModelID,
  start,
  trainingOptions
}: TrainModalProps) => {
  const [filterRows, setFilterRows] = useState<{ filter: (row: TableRow) => boolean }>({
    filter: () => true
  });
  const [isTraining, setIsTraining] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [language, setLanguage] = useState(
    mode === 'edit' && selectedModel?.language ? selectedModel.language : 'english'
  );
  const [modelDescription, setModelDescription] = useState(
    mode === 'edit' && selectedModel?.description ? selectedModel.description : ''
  );
  const [modelName, setModelName] = useState(
    mode === 'edit' && selectedModel?.name ? selectedModel.name : 'New AdCopy AI Model'
  );
  const [selectAll, setSelectAll] = useState(false);
  const [selectedAccountID, setSelectedAccountID] = useState<number | 'All'>('All');
  const [selectedAdGroupID, setSelectedAdGroupID] = useState<number | 'All'>('All');
  const [selectedCampaignID, setSelectedCampaignID] = useState<number | 'All'>('All');
  const [selectedPlatform, setSelectedPlatform] = useState<AdPlatform | 'All'>('All');
  const [selectedRows, setSelectedRows] = useState<SelectedRowsMap>({});
  const [showSpendAndPerformance, setShowSpendAndPerformance] = useState(true);
  const companyInfo = useCompanyInfo();
  const company = companyInfo.cid;
  const setError = useSetError();

  const dateRange = useMemo(() => ({ start, end }), [start, end]);

  const [accountOptions, adGroupOptions, campaignOptions, platformOptions] = useMemo(() => {
    const platformLabelMap: Record<string, string> = {};
    const accountIDMap: Record<string, string> = {};
    const campaignIDMap: Record<string, string> = {};
    const adGroupIDMap: Record<string, string> = {};
    for (const row of trainingOptions ?? []) {
      platformLabelMap[row.platform] = PLATFORM_LABELS[row.platform] ?? row.platform;
      // Platform is the top of the hierarchy: only surface accounts/campaigns/ad groups for the
      // selected platform.
      if (selectedPlatform !== 'All' && row.platform !== selectedPlatform) continue;
      accountIDMap[row.accountID] = row.accountName;
      campaignIDMap[row.campaignID] = row.campaignName;
      adGroupIDMap[row.adGroupID] = row.adGroupName;
    }
    const acctOpts = [
      { value: 'All', label: 'All Accounts' },
      ...Object.keys(accountIDMap)
        .map(id => ({ value: id, label: `${accountIDMap[id]} [${id}]` }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
    const adGrpOpts = [
      { value: 'All', label: 'All Ad Groups' },
      ...Object.keys(adGroupIDMap)
        .map(id => ({ value: id, label: `${adGroupIDMap[id]} [${id}]` }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
    const campOpts = [
      { value: 'All', label: 'All Campaigns' },
      ...Object.keys(campaignIDMap)
        .map(id => ({ value: id, label: `${campaignIDMap[id]} [${id}]` }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
    const platformOpts = [
      { value: 'All', label: 'All Platforms' },
      ...Object.keys(platformLabelMap)
        .map(platform => ({ value: platform, label: platformLabelMap[platform] }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
    return [acctOpts, adGrpOpts, campOpts, platformOpts];
  }, [trainingOptions, selectedPlatform]);

  const filteredRows = useMemo(
    () =>
      (trainingOptions ?? []).filter(
        row =>
          row.impressions !== 0 &&
          filterRows.filter(row) &&
          (selectedPlatform === 'All' || row.platform === selectedPlatform) &&
          (selectedAccountID === 'All' || row.accountID === selectedAccountID) &&
          (selectedCampaignID === 'All' || row.campaignID === selectedCampaignID) &&
          (selectedAdGroupID === 'All' || row.adGroupID === selectedAdGroupID)
      ),
    [
      trainingOptions,
      filterRows,
      selectedPlatform,
      selectedAccountID,
      selectedCampaignID,
      selectedAdGroupID
    ]
  );

  const renameModel = useCallback(() => {
    (async () => {
      try {
        setIsTraining(true);
        const trainingInstance = await addCopyTigerModelInstance({
          company,
          description: modelDescription.trim(),
          id: mode === 'edit' ? selectedModel?.id : undefined,
          language,
          name: modelName.trim()
        });
        setSelectedModelID(trainingInstance.id);
        setSelectedRows({});
        setSelectAll(false);
        onHide(true);
        setDialog({
          title: 'Model Renamed',
          body: `${modelName.trim()} has been successfully renamed.`,
          intent: Intent.SUCCESS
        });
      } catch (e) {
        throw new Error(`Failed to rename model. Error: ${getSafeErrorMessage(e)}`, { cause: e });
      } finally {
        setIsTraining(false);
      }
    })();
  }, [
    company,
    language,
    mode,
    modelDescription,
    modelName,
    onHide,
    selectedModel,
    setDialog,
    setSelectedModelID
  ]);

  const trainModel = useCallback(async () => {
    const selectedRowsArray = Object.values(selectedRows);
    const trimmedName = modelName.trim();
    const trimmedDescription = modelDescription.trim();
    const validationError = validateTrainingInputs({
      selectedCount: selectedRowsArray.length,
      modelName: trimmedName,
      modelDescription: trimmedDescription
    });
    if (validationError) {
      setDialog(validationError);
      return;
    }
    try {
      setIsTraining(true);
      const vectorstoreData = await kickOffAndPollTraining({
        lambdaArgs: { company, training_ads: buildTrainingAds(selectedRowsArray) }
      });
      const trainingInstance = await addCopyTigerModelInstance({
        adsUsed: selectedRowsArray.map(row => row.id).filter((id): id is string => Boolean(id)),
        company,
        description: trimmedDescription,
        id: mode === 'edit' ? selectedModel?.id : undefined,
        language,
        name: trimmedName,
        vectorstoreData
      });
      setSelectedModelID(trainingInstance.id);
      setSelectedRows({});
      setSelectAll(false);
      onHide(true);
      setDialog({
        title: 'Model Trained',
        body: `${trimmedName} has been successfully trained.`,
        intent: Intent.SUCCESS
      });
    } catch (e) {
      setError({ message: getSafeErrorMessage(e), reportError: e });
    } finally {
      setIsTraining(false);
    }
  }, [
    company,
    language,
    mode,
    modelDescription,
    modelName,
    onHide,
    selectedModel,
    selectedRows,
    setDialog,
    setError,
    setSelectedModelID
  ]);

  const checkboxRenderer = useCallback(
    (row: TableRow) => (
      <Checkbox
        checked={Object.hasOwn(selectedRows, row.id)}
        onChange={() => {
          if (selectedRows[row.id]) {
            setSelectedRows(current => {
              const { [row.id]: _, ...rest } = current;
              return rest;
            });
          } else {
            setSelectedRows(current => ({
              ...current,
              [row.id]: row
            }));
          }
        }}
      />
    ),
    [selectedRows]
  );

  const columnWidths = useMemo(() => {
    const widths: Record<string, number> = {};
    for (const row of filteredRows) {
      for (const key of Object.keys(row)) {
        widths[key] =
          key === 'id'
            ? 50
            : Math.min(
                MAX_COL_WIDTH,
                Math.max(
                  widths[key] ?? MIN_COL_WIDTH,
                  COL_PADDING + getTextWidth(`${row[key]}`, 12, 600)
                )
              );
      }
    }
    return widths;
  }, [filteredRows]);

  const baseHeaders: Header[] = useMemo(
    () => [
      { label: '', name: 'id', nonInteractive: true, renderer: checkboxRenderer },
      {
        contentGetter: (row: TableRow) => PLATFORM_LABELS[row.platform] ?? row.platform,
        label: 'Platform',
        name: 'platform'
      },
      {
        contentGetter: (row: TableRow) => row.accountID,
        label: 'Account ID',
        name: 'accountID'
      },
      {
        contentGetter: (row: TableRow) => row.accountName ?? '',
        label: 'Account Name',
        name: 'accountName'
      },
      {
        contentGetter: (row: TableRow) => row.campaignID,
        label: 'Campaign ID',
        name: 'campaignID'
      },
      {
        contentGetter: (row: TableRow) => row.campaignName ?? '',
        label: 'Campaign Name',
        name: 'campaignName'
      },
      {
        contentGetter: (row: TableRow) => row.adGroupID,
        label: 'Ad Group ID',
        name: 'adGroupID'
      },
      {
        contentGetter: (row: TableRow) => row.adGroupName ?? '',
        label: 'Ad Group Name',
        name: 'adGroupName'
      },
      {
        contentGetter: (row: TableRow) => row.keywords.join(','),
        label: 'Keywords',
        name: 'keywords'
      }
    ],
    [checkboxRenderer]
  );

  const performanceHeaders: Header[] = useMemo(() => {
    if (!showSpendAndPerformance) return [];
    return [
      {
        contentGetter: (row: TableRow) => row.impressions,
        label: 'Imps',
        name: 'impressions'
      },
      {
        contentGetter: (row: TableRow) => Math.round(row.spend),
        label: 'Spend',
        name: 'spend',
        renderer: (row: TableRow) => `$${Math.round(row.spend).toLocaleString('en-US')}`
      },
      {
        contentGetter: (row: TableRow) => row.clicks,
        label: 'Clicks',
        name: 'clicks'
      }
    ];
  }, [showSpendAndPerformance]);

  const tableHeaders: Header[] = useMemo(
    () =>
      [
        ...baseHeaders,
        ...performanceHeaders,
        ...ADDITIONAL_HEADERS,
        ...DESCRIPTION_HEADERS,
        ...HEADLINE_HEADERS
      ].map(header => {
        const dataWidth = header?.name
          ? (columnWidths[header.name] ?? MIN_COL_WIDTH)
          : MIN_COL_WIDTH;
        const labelWidth = header.label ? COL_PADDING + getTextWidth(header.label, 12, 600) : 0;
        return {
          ...header,
          flex: true,
          minFlexWidth: Math.min(MAX_COL_WIDTH, Math.max(dataWidth, labelWidth))
        };
      }),
    [baseHeaders, performanceHeaders, columnWidths]
  );

  const headersRenderer = useCallback(
    ({ data, columnIndex }) => {
      if (columnIndex === 0) {
        return (
          <Checkbox
            id='select-all-training-rows'
            label='Include'
            checked={selectAll}
            onChange={() => {
              if (selectAll) {
                setSelectedRows({});
              } else {
                const all: Record<string, TableRow> = {};
                for (const row of filteredRows) {
                  all[row.id.toString()] = row;
                }
                setSelectedRows(all);
              }
              setSelectAll(!selectAll);
            }}
          />
        );
      }
      return data;
    },
    [selectAll, filteredRows]
  );

  return (
    <Modal show onHide={() => onHide()} size='xl' className='copytiger-train-modal'>
      {isTraining && <FullPageSpinner transparent />}
      <Modal.Header className='copytiger-train-modal-header'>
        <div className='train-modal-header-content'>
          <div className='train-modal-title-row'>
            <div className='train-modal-title-with-icon'>
              <div className='train-modal-title'>Train</div>
              {isTitleEditing ? (
                <Form.Control
                  value={modelName}
                  onChange={e => setModelName(e.target.value)}
                  onBlur={() => setIsTitleEditing(false)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      setIsTitleEditing(false);
                    }
                  }}
                  autoFocus
                  className='train-modal-title-input'
                  placeholder='Enter model name'
                />
              ) : (
                <div className='train-modal-title'>{modelName}</div>
              )}
              <InteractiveDiv
                className='train-modal-edit-icon'
                onClick={() => setIsTitleEditing(!isTitleEditing)}
              >
                {isTitleEditing ? <MdSave /> : <MdEdit />}
              </InteractiveDiv>
            </div>
          </div>
          <div className='train-modal-description-row'>
            <Form.Control
              placeholder='Short description 150 characters'
              value={modelDescription}
              onChange={e => setModelDescription(e.target.value)}
              maxLength={150}
              className='train-modal-description-input-full'
            />
          </div>
          <div className='train-modal-controls-section'>
            <div>
              {platformOptions.length > 1 && (
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  value={selectedPlatform}
                  options={platformOptions}
                  onChange={value => {
                    setSelectedPlatform(value === 'All' ? 'All' : (value as AdPlatform));
                    setSelectedAccountID('All');
                    setSelectedCampaignID('All');
                    setSelectedAdGroupID('All');
                  }}
                  placeholder='Filter by Platform'
                  size={Size.SM}
                />
              )}
            </div>
            <div>
              {accountOptions.length > 2 && (
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  value={`${selectedAccountID}`}
                  options={accountOptions}
                  onChange={value => {
                    setSelectedAccountID(value === 'All' ? 'All' : parseInt(value, 10));
                    setSelectedCampaignID('All');
                    setSelectedAdGroupID('All');
                  }}
                  size={Size.SM}
                />
              )}
            </div>
            <div>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                disabled={accountOptions.length > 2 && selectedAccountID === 'All'}
                value={`${selectedCampaignID}`}
                options={campaignOptions}
                onChange={value => {
                  setSelectedCampaignID(value === 'All' ? 'All' : parseInt(value, 10));
                  setSelectedAdGroupID('All');
                }}
                placeholder='Filter by Campaign'
                size={Size.SM}
              />
            </div>
            <div className='copytiger-adGroupFilterContainer'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                disabled={selectedCampaignID === 'All'}
                value={`${selectedAdGroupID}`}
                options={adGroupOptions}
                onChange={value =>
                  setSelectedAdGroupID(value === 'All' ? 'All' : parseInt(value, 10))
                }
                placeholder='Filter by Ad Group'
                size={Size.SM}
              />
            </div>
            <div className='train-modal-language-dropdown'>
              <div className='train-modal-dropdown-label'>Model Training Language:</div>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                value={language}
                options={Object.keys(LANGUAGE_OPTIONS_MAP).map(key => ({
                  value: key,
                  label: LANGUAGE_OPTIONS_MAP[key]
                }))}
                onChange={setLanguage}
                size={Size.SM}
              />
            </div>
          </div>
        </div>
        <IconButton
          className='train-modal-close-icon'
          appearance={Appearance.EMPTY}
          onClick={() => onHide()}
        >
          <MdClose />
        </IconButton>
      </Modal.Header>
      <Modal.Body className='copytiger-train-modal-body'>
        <div className='bpm-copytiger'>
          <div className='copytiger-container'>
            <div className='copytiger-top-section'>
              <div className='copytiger-table-container fill-parent'>
                <div className='table-controls-container'>
                  <div className='filters-container'>
                    <div className='copytiger-spend-performance-toggle-container'>
                      <ToggleSwitch
                        label='Show spend and performance'
                        checked={showSpendAndPerformance}
                        onChange={setShowSpendAndPerformance}
                        size={Size.SM}
                      />
                    </div>
                    <DatePicker
                      datePresets
                      background={Background.LIGHT}
                      onChange={onDateRangeChange}
                      range={dateRange}
                      removeHeaderText
                      size={Size.SM}
                      design={Design.NEUTRAL}
                    />
                  </div>
                  <FilterBar
                    hasAdvanced
                    onFilter={filter => {
                      setFilterRows({ filter });
                    }}
                    options={
                      filteredRows.length > 0
                        ? Object.keys(filteredRows[0]).filter(
                            field => field !== 'id' && typeof filteredRows[0][field] === 'string'
                          )
                        : []
                    }
                    size={Size.LG}
                    lines={filteredRows}
                  />
                  <DownloadDropdown
                    size={Size.SM}
                    design={Design.BRAND}
                    onClickOptions={[() => exportToExcel(filteredRows, `${company}_trainingRows`)]}
                    menuOptions={['XLSX']}
                  />
                </div>
                {isLoadingTrainingOptions && (
                  <div className='spinner-center'>
                    <Spinner size={100} />
                  </div>
                )}
                {!isLoadingTrainingOptions &&
                  trainingOptions != null &&
                  trainingOptions.length > 0 && (
                    <BPMTable
                      data={filteredRows}
                      filterBar={false}
                      headers={tableHeaders}
                      headersRenderer={headersRenderer}
                      minColumnWidth={1}
                      rowHeight={75}
                    />
                  )}
                {!isLoadingTrainingOptions &&
                  (trainingOptions == null || trainingOptions.length === 0) && (
                    <div className='copytiger-no-data'>
                      No data for {dateRange.start} - {dateRange.end}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className='copytiger-train-modal-footer'>
          <div className='footer-left-section'>
            {mode === 'edit' && (
              <Button
                leadingElement={<MdDelete />}
                onClick={deleteModel}
                appearance={Appearance.OUTLINED}
                design={Design.NEUTRAL}
              >
                Delete Model
              </Button>
            )}
          </div>
          <div className='footer-right-section'>
            <div className='selected-rows-display-inline'>
              Selected Training Data ({Object.keys(selectedRows).length} rows)
            </div>
            <div className='selected-rows-button-wrapper-inline'>
              {mode === 'edit' && (
                <Button
                  appearance={Appearance.OUTLINED}
                  leadingElement={<MdEdit />}
                  onClick={renameModel}
                  design={Design.NEUTRAL}
                >
                  Rename Model
                </Button>
              )}
              <Button leadingElement={<MdOutlineAutoAwesome />} onClick={trainModel}>
                {mode === 'edit' ? 'Update Model' : 'Train Model'}
              </Button>
            </div>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
